import { OcppError, ProtocolCatalog } from "./catalog";

// OCPP-J message types. CALLRESULTERROR and SEND exist from OCPP 2.1 on.
export const CALL = 2;
export const CALLRESULT = 3;
export const CALLERROR = 4;
export const CALLRESULTERROR = 5;
export const SEND = 6;

export type FrameKind = "CALL" | "CALLRESULT" | "CALLERROR" | "CALLRESULTERROR" | "SEND";
const KIND: Record<number, FrameKind> = {
  [CALL]: "CALL",
  [CALLRESULT]: "CALLRESULT",
  [CALLERROR]: "CALLERROR",
  [CALLRESULTERROR]: "CALLRESULTERROR",
  [SEND]: "SEND",
};

/** The subset of the WebSocket API used here (browser WebSocket and Node's global one fit). */
export interface WebSocketLike {
  readonly readyState: number;
  readonly protocol: string;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((event: any) => void) | null;
  onmessage: ((event: { data: any }) => void) | null;
  onclose: ((event: { code: number; reason: string }) => void) | null;
  onerror: ((event: any) => void) | null;
}

export interface RpcFrameLog {
  direction: "out" | "in";
  kind: FrameKind | "INVALID";
  messageId?: string;
  action?: string;
  raw: string;
}

export interface RpcConnectionOptions {
  socket: WebSocketLike;
  catalog: ProtocolCatalog;
  /** Handles a CSMS-initiated CALL; throw OcppError to answer with a CALLERROR. */
  onCall: (action: string, params: any) => Promise<any>;
  onFrame?: (entry: RpcFrameLog) => void;
  callTimeoutMs?: number;
  newMessageId?: () => string;
}

const OPEN = 1;

export const randomMessageId = (): string => {
  const cryptoApi: any = (globalThis as any).crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

interface PendingCall {
  action: string;
  resolve: (value: any) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * OCPP-J RPC over one WebSocket. Outgoing CALLs are serialised: OCPP-J lets a peer have only one
 * CALL outstanding at a time, so `call()` queues until the previous one is answered or timed out.
 * Requests and responses in both directions are validated against the version's schemas.
 */
export class OcppRpcConnection {
  private readonly socket: WebSocketLike;
  private readonly catalog: ProtocolCatalog;
  private readonly options: RpcConnectionOptions;
  private readonly callTimeoutMs: number;
  private readonly newMessageId: () => string;
  private pending: { id: string; call: PendingCall } | null = null;
  private queue: Promise<unknown> = Promise.resolve();
  private closed = false;

  constructor(options: RpcConnectionOptions) {
    this.options = options;
    this.socket = options.socket;
    this.catalog = options.catalog;
    this.callTimeoutMs = options.callTimeoutMs ?? 30_000;
    this.newMessageId = options.newMessageId ?? randomMessageId;
  }

  get protocol() {
    return this.catalog.protocol;
  }

  /** Feed one received WebSocket message. */
  receive(data: unknown): void {
    const raw = typeof data === "string" ? data : String(data);
    let frame: any;
    try {
      frame = JSON.parse(raw);
    } catch {
      this.log({ direction: "in", kind: "INVALID", raw });
      this.sendFrame([CALLERROR, "-1", "RpcFrameworkError", "Message must be a JSON structure", {}]);
      return;
    }
    if (!Array.isArray(frame) || typeof frame[1] !== "string") {
      this.log({ direction: "in", kind: "INVALID", raw });
      this.sendFrame([CALLERROR, "-1", "RpcFrameworkError", "Message must be an OCPP-J array", {}]);
      return;
    }

    const [type, messageId] = frame;
    const kind = KIND[type];
    this.log({ direction: "in", kind: kind ?? "INVALID", messageId, action: type === CALL ? frame[2] : this.pendingAction(messageId), raw });

    switch (type) {
      case CALL:
        void this.handleCall(messageId, frame[2], frame[3] ?? {});
        return;
      case CALLRESULT:
        this.settle(messageId, frame[2]);
        return;
      case CALLERROR:
        this.fail(messageId, new OcppError(String(frame[2]), String(frame[3] ?? ""), frame[4] ?? {}));
        return;
      case CALLRESULTERROR:
      case SEND:
        // Never answered. A CSMS sends neither in OCPP 2.1, so there is nothing to act on.
        return;
      default:
        this.sendFrame([CALLERROR, messageId, "MessageTypeNotSupported", `Unknown message type ${type}`, {}]);
    }
  }

  /** Send a CALL and wait for its CALLRESULT. Rejects with OcppError on CALLERROR or timeout. */
  call(action: string, params: any = {}): Promise<any> {
    const run = () => this.doCall(action, params);
    const result = this.queue.then(run, run);
    this.queue = result.catch(() => undefined);
    return result;
  }

  /** Send an OCPP 2.1 SEND (unconfirmed) message. */
  send(action: string, params: any): void {
    if (!this.catalog.sendMessages.has(action)) {
      throw new OcppError("NotSupported", `${action} is not a SEND message in ${this.protocol}`);
    }
    this.catalog.validate(action, "req", params);
    this.sendFrame([SEND, this.newMessageId(), action, params]);
  }

  close(): void {
    this.closed = true;
    if (this.pending) {
      clearTimeout(this.pending.call.timer);
      this.pending.call.reject(new OcppError("GenericError", "Connection closed"));
      this.pending = null;
    }
  }

  private async doCall(action: string, params: any): Promise<any> {
    if (this.closed || this.socket.readyState !== OPEN) {
      throw new OcppError("GenericError", `Cannot send ${action}: not connected`);
    }
    if (!this.catalog.cpInitiated.has(action) || this.catalog.sendMessages.has(action)) {
      throw new OcppError("NotSupported", `${action} is not a charge point CALL in ${this.protocol}`);
    }
    this.catalog.validate(action, "req", params);

    const id = this.newMessageId();
    const response = new Promise<any>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.pending?.id === id) this.pending = null;
        reject(new OcppError("Timeout", `${action} was not answered within ${this.callTimeoutMs} ms`));
      }, this.callTimeoutMs);
      this.pending = { id, call: { action, resolve, reject, timer } };
    });
    this.sendFrame([CALL, id, action, params], action);
    const result = await response;
    this.catalog.validate(action, "conf", result);
    return result;
  }

  private pendingAction(messageId: string) {
    return this.pending?.id === messageId ? this.pending.call.action : undefined;
  }

  private settle(messageId: string, result: any) {
    if (this.pending?.id !== messageId) return;
    const { call } = this.pending;
    this.pending = null;
    clearTimeout(call.timer);
    call.resolve(result);
  }

  private fail(messageId: string, error: OcppError) {
    if (this.pending?.id !== messageId) return;
    const { call } = this.pending;
    this.pending = null;
    clearTimeout(call.timer);
    call.reject(error);
  }

  private async handleCall(messageId: string, action: unknown, params: any) {
    try {
      if (typeof action !== "string") throw new OcppError("RpcFrameworkError", "Action must be a string");
      if (!this.catalog.actions.includes(action)) {
        throw new OcppError("NotImplemented", `${action} is not an ${this.protocol} message`);
      }
      if (!this.catalog.csmsInitiated.has(action)) {
        throw new OcppError("NotSupported", `${action} is not initiated by the CSMS in ${this.protocol}`);
      }
      this.catalog.validate(action, "req", params);
      const result = await this.options.onCall(action, params);
      try {
        this.catalog.validate(action, "conf", result);
      } catch (error: any) {
        // Our own answer is malformed: report it instead of sending an invalid CALLRESULT.
        throw new OcppError("InternalError", `Invalid ${action} response: ${error.message}`);
      }
      this.sendFrame([CALLRESULT, messageId, result], action);
    } catch (error: any) {
      const code = error instanceof OcppError ? error.code : "InternalError";
      const details = error instanceof OcppError ? { message: error.message } : {};
      this.sendFrame([CALLERROR, messageId, code, String(error?.message ?? error).slice(0, 255), details], String(action));
    }
  }

  private sendFrame(frame: unknown[], action?: string) {
    if (this.socket.readyState !== OPEN) return;
    const raw = JSON.stringify(frame);
    this.log({ direction: "out", kind: KIND[frame[0] as number], messageId: frame[1] as string, action, raw });
    this.socket.send(raw);
  }

  private log(entry: RpcFrameLog) {
    this.options.onFrame?.(entry);
  }
}
